import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { Building, buildingService } from "../services/buildingService";
import { useAuth } from "./AuthContext";

interface BuildingContextType {
  currentBuilding: Building | null;
  buildings: Building[];
  isLoading: boolean;
  error: string | null;
  selectBuilding: (building: Building) => void;
  clearBuilding: () => void;
  refreshBuildings: () => Promise<Building[]>;
}

const STORAGE_KEY = "sbm_selected_building_id";
const LEGACY_STORAGE_KEY = "sbm_selected_building";
const BuildingContext = createContext<BuildingContextType | undefined>(undefined);

function storedBuildingId(): number | null {
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (stored && Number.isInteger(Number(stored))) return Number(stored);

  const legacy = sessionStorage.getItem(LEGACY_STORAGE_KEY);
  if (legacy) {
    try {
      const parsed = JSON.parse(legacy) as { id?: number };
      if (Number.isInteger(parsed.id)) return parsed.id ?? null;
    } catch {
      // Invalid legacy state is discarded below.
    }
  }
  return null;
}

export function BuildingProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [currentBuilding, setCurrentBuilding] = useState<Building | null>(null);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [isLoading, setIsLoading] = useState(Boolean(user));
  const [error, setError] = useState<string | null>(null);

  const clearBuilding = useCallback(() => {
    setCurrentBuilding(null);
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_STORAGE_KEY);
  }, []);

  const refreshBuildings = useCallback(async (): Promise<Building[]> => {
    setIsLoading(true);
    setError(null);
    try {
      const authorized = await buildingService.getAuthorizedBuildings();
      setBuildings(authorized);

      const selectedId = currentBuilding?.id ?? storedBuildingId();
      // `/me/buildings` already applies the role-specific access rules. Keep
      // archived administrator selections valid; those sites are omitted from
      // property-manager responses by the API.
      const selected = authorized.find((building) => building.id === selectedId);
      if (selected) {
        setCurrentBuilding(selected);
        sessionStorage.setItem(STORAGE_KEY, String(selected.id));
      } else {
        clearBuilding();
      }
      return authorized;
    } catch (refreshError) {
      const message = refreshError instanceof Error ? refreshError.message : "Failed to fetch buildings";
      setError(message);
      setBuildings([]);
      clearBuilding();
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [clearBuilding, currentBuilding?.id]);

  useEffect(() => {
    if (!user) {
      setBuildings([]);
      clearBuilding();
      setIsLoading(false);
      return;
    }
    void refreshBuildings();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectBuilding = useCallback((building: Building) => {
    setCurrentBuilding(building);
    sessionStorage.setItem(STORAGE_KEY, String(building.id));
    sessionStorage.removeItem(LEGACY_STORAGE_KEY);
  }, []);

  return (
    <BuildingContext.Provider
      value={{ currentBuilding, buildings, isLoading, error, selectBuilding, clearBuilding, refreshBuildings }}
    >
      {children}
    </BuildingContext.Provider>
  );
}

// The provider and its hook intentionally share this context module.
// eslint-disable-next-line react-refresh/only-export-components
export function useBuilding(): BuildingContextType {
  const context = useContext(BuildingContext);
  if (!context) throw new Error("useBuilding must be used within a BuildingProvider");
  return context;
}

export default BuildingContext;
